import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    IconButton,
    Typography,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";

function OverrideDayDialog({ open, onClose, dayPlan, machineId, overrideMutation, refetchContext }) {
    const theme = useTheme();
    const [plannedHours, setPlannedHours] = useState("");
    const [shifts, setShifts] = useState([]);

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                  "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                  },
              }
            : {};

    useEffect(() => {
        if (dayPlan) {
            setPlannedHours(dayPlan.plannedProductionHours?.toString() ?? "");
            setShifts(dayPlan.shifts ? [...dayPlan.shifts] : []);
        } else {
            setPlannedHours("");
            setShifts([]);
        }
    }, [dayPlan]);

    const handleShiftChange = (index, field, value) => {
        const updated = [...shifts];
        updated[index] = { ...updated[index], [field]: value };
        setShifts(updated);
    };

    const handleAddShift = () => {
        setShifts([
            ...shifts,
            {
                startTime: "06:00:00",
                endTime: "14:00:00",
                label: "Nytt skift",
            },
        ]);
    };

    const handleRemoveShift = (index) => {
        setShifts(shifts.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!dayPlan || !machineId) {
            onClose();
            return;
        }

        const payload = {
            machineId,
            date: dayPlan.date, // backend tar DateTime
            plannedProductionHours: parseFloat(plannedHours) || 0,
            shifts: shifts.map((s) => ({
                startTime: s.startTime,
                endTime: s.endTime,
                label: s.label,
            })),
        };

        await overrideMutation.mutateAsync({
            ...payload,
            __refetch: refetchContext,
        });

        onClose();
    };

    const dateLabel = dayPlan ? dayjs(dayPlan.date).format("YYYY-MM-DD") : "";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Överstyr dag</DialogTitle>
            <DialogContent dividers>
                {dayPlan && (
                    <>
                        <Typography variant="subtitle1" gutterBottom>
                            Maskin: {machineId} &mdash; Datum: {dateLabel}
                        </Typography>

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Planerade timmar"
                            type="number"
                            value={plannedHours}
                            onChange={(e) => setPlannedHours(e.target.value)}
                        />

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            Skift den här dagen
                        </Typography>

                        {shifts.map((shift, idx) => (
                            <Stack
                                key={idx}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                mb={1}
                            >
                                <TextField
                                    label="Start"
                                    type="time"
                                    size="small"
                                    value={shift.startTime.substring(0, 5)}
                                    onChange={(e) =>
                                        handleShiftChange(
                                            idx,
                                            "startTime",
                                            e.target.value + ":00"
                                        )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    sx={dateTimeIconSx}
                                />
                                <TextField
                                    label="Slut"
                                    type="time"
                                    size="small"
                                    value={shift.endTime.substring(0, 5)}
                                    onChange={(e) =>
                                        handleShiftChange(
                                            idx,
                                            "endTime",
                                            e.target.value + ":00"
                                        )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    sx={dateTimeIconSx}
                                />
                                <TextField
                                    label="Namn"
                                    size="small"
                                    value={shift.label}
                                    onChange={(e) =>
                                        handleShiftChange(
                                            idx,
                                            "label",
                                            e.target.value
                                        )
                                    }
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveShift(idx)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}

                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            sx={{ mt: 1 }}
                            onClick={handleAddShift}
                        >
                            Lägg till skift
                        </Button>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Avbryt</Button>
                <Button variant="contained" onClick={handleSave}>
                    Spara överstyrning
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default OverrideDayDialog;
